// src/features/withholding_docs/withholding_docsApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

const withholdingDocsAdapter = createEntityAdapter({
  selectId: (doc) => doc.id,
});

const initialState = withholdingDocsAdapter.getInitialState({ total: 0 });

export const withholding_docsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getWithholding_docs: builder.query({
      query: (params = {}) => ({
        url: '/withholding_docs',
        params,
      }),

      transformResponse: (response, meta) => {
        let items = [];
        let total = 0;

        if (
          response &&
          typeof response === 'object' &&
          'items' in response &&
          'total' in response
        ) {
          items = response.items ?? [];
          total = Number(response.total ?? 0);
        } else if (Array.isArray(response)) {
          items = response;
          const headerTotal =
            meta?.response?.headers?.get('X-Total-Count') ??
            meta?.response?.headers?.get('x-total-count');

          total = headerTotal ? Number(headerTotal) : items.length;
        }

        const processed = items.map((doc) => {
          const sid = String(
            doc?.withholding_doc_id ?? doc?.id ?? doc?._id ?? ''
          );

          return {
            ...doc,
            id: sid,
            _id: sid,
          };
        });

        return withholdingDocsAdapter.setAll(
          { ...initialState, total },
          processed
        );
      },

      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'WithholdingDoc', id })),
              { type: 'WithholdingDoc', id: 'LIST' },
            ]
          : [{ type: 'WithholdingDoc', id: 'LIST' }],
    }),

    getWithholding_docById: builder.query({
      query: (id) => `/withholding_docs/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'WithholdingDoc', id }],
    }),

    addNewWithholding_doc: builder.mutation({
      query: (body) => ({
        url: '/withholding_docs',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'WithholdingDoc', id: 'LIST' }],
    }),

    updateWithholding_doc: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/withholding_docs/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'WithholdingDoc', id },
        { type: 'WithholdingDoc', id: 'LIST' },
      ],
    }),

    deleteWithholding_doc: builder.mutation({
      query: ({ id }) => ({
        url: `/withholding_docs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'WithholdingDoc', id },
        { type: 'WithholdingDoc', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetWithholding_docsQuery,
  useGetWithholding_docByIdQuery,
  useAddNewWithholding_docMutation,
  useUpdateWithholding_docMutation,
  useDeleteWithholding_docMutation,
} = withholding_docsApiSlice;

const selectGetWithholding_docsResult =
  withholding_docsApiSlice.endpoints.getWithholding_docs.select;

export const makeSelectWithholding_docsData = (args) =>
  createSelector(
    selectGetWithholding_docsResult(args),
    (res) => res?.data ?? initialState
  );

export const makeWithholding_docSelectors = (args) => {
  const selectData = makeSelectWithholding_docsData(args);
  const baseSelectors = withholdingDocsAdapter.getSelectors((state) =>
    selectData(state)
  );

  return {
    selectAllWithholding_docs: baseSelectors.selectAll,
    selectWithholding_docById: baseSelectors.selectById,
    selectWithholding_docIds: baseSelectors.selectIds,
    selectWithholding_docEntities: baseSelectors.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};